// Table of contents component for document and collection pages.

import React, { useEffect, useState } from 'react';
import { ChevronRightIcon } from 'lucide-react';
import { useLocation } from 'react-router';
import { dispatchDocumentSectionNavigation } from '@/content/lib/anchors';
import type { OutlineItem } from '@/content/lib/outline';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/utilities/cn';

interface TableOfContentsProps {
  items: OutlineItem[];
}

const OutlineList: React.FC<{
  items: OutlineItem[];
  activeId?: string;
  depth?: number;
}> = ({ items, activeId, depth = 0 }) => (
  <ol className="flex flex-col gap-1">
    {items.map((item) => (
      <OutlineItemRow
        key={item.id}
        item={item}
        activeId={activeId}
        depth={depth}
      />
    ))}
  </ol>
);

const outlineItemContainsId = (item: OutlineItem, id: string): boolean =>
  item.id === id ||
  item.children.some((child) => outlineItemContainsId(child, id));

const outlineRowClassName = (active: boolean) =>
  cn(
    'w-full justify-start overflow-hidden text-muted-foreground transition-none hover:bg-muted hover:text-foreground aria-expanded:bg-transparent aria-expanded:text-muted-foreground data-[state=open]:bg-transparent data-[state=open]:text-muted-foreground data-[state=open]:hover:bg-muted data-[state=open]:hover:text-foreground',
    active &&
      'font-medium !text-foreground hover:!text-foreground aria-expanded:!text-foreground data-[state=open]:!text-foreground data-[state=open]:hover:!text-foreground'
  );

const OutlineItemRow: React.FC<{
  item: OutlineItem;
  activeId?: string;
  depth: number;
}> = ({ item, activeId, depth }) => {
  const hasChildren = item.children.length > 0;
  const active = activeId === item.id;
  const hashTargetsItem = Boolean(
    activeId && outlineItemContainsId(item, activeId)
  );
  const [open, setOpen] = useState(hashTargetsItem);

  useEffect(() => {
    if (hashTargetsItem) {
      setOpen(true);
    }
  }, [hashTargetsItem]);

  const handleLinkClick = () => {
    dispatchDocumentSectionNavigation(item.id);

    if (hasChildren) {
      setOpen(true);
    }
  };

  if (hasChildren) {
    return (
      <li>
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={cn('group/toc-row', outlineRowClassName(active))}
              asChild
            >
              <a
                href={`#${item.id}`}
                onClick={handleLinkClick}
                aria-current={active ? 'location' : undefined}
              >
                <ChevronRightIcon
                  data-icon="inline-start"
                  className="transition-transform group-data-[state=open]/toc-row:rotate-90"
                  aria-hidden="true"
                />
                <span className="min-w-0 truncate">{item.title}</span>
              </a>
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-1 ml-3">
            <OutlineList
              items={item.children}
              activeId={activeId}
              depth={depth + 1}
            />
          </CollapsibleContent>
        </Collapsible>
      </li>
    );
  }

  return (
    <li>
      <Button
        variant="ghost"
        size="sm"
        className={outlineRowClassName(active)}
        asChild
      >
        <a
          href={`#${item.id}`}
          onClick={handleLinkClick}
          aria-current={active ? 'location' : undefined}
        >
          <span
            data-icon="inline-start"
            className="size-3.5 shrink-0"
            aria-hidden="true"
          />
          <span className="min-w-0 truncate">{item.title}</span>
        </a>
      </Button>
    </li>
  );
};

const TableOfContents: React.FC<TableOfContentsProps> = ({ items }) => {
  const location = useLocation();
  const activeId = location.hash.replace('#', '') || undefined;
  const [open, setOpen] = useState(true);

  if (items.length === 0) {
    return null;
  }

  return (
    <nav aria-label="Table of contents">
      <Collapsible open={open} onOpenChange={setOpen}>
        <div className="flex flex-col gap-2">
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="group/toc-title w-full justify-start text-xs font-semibold uppercase tracking-wide text-muted-foreground transition-none hover:bg-muted hover:text-foreground aria-expanded:bg-transparent aria-expanded:text-muted-foreground data-[state=open]:bg-transparent data-[state=open]:text-muted-foreground data-[state=open]:hover:bg-muted data-[state=open]:hover:text-foreground"
            >
              <ChevronRightIcon
                data-icon="inline-start"
                className="transition-transform group-data-[state=open]/toc-title:rotate-90"
                aria-hidden="true"
              />
              Contents
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <OutlineList items={items} activeId={activeId} />
          </CollapsibleContent>
        </div>
      </Collapsible>
    </nav>
  );
};

export default TableOfContents;
